import { useForm, type UseFormSetError } from "react-hook-form";
import { Button, Dialog, DialogActions, DialogContent, DialogHeader, DialogTitle } from "~/common/mui";
import { useTranslation } from "react-i18next";
import { RequestAQuoteForm } from "./RequestAQuoteForm";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { zRequiredEmail, zRequiredString } from "~/common/utils/schemaUtils";

const requestAQuoteSchema = z.object({
  company: z.string().optional(),
  firstName: zRequiredString("common.form.validation.required"),
  lastName: zRequiredString("common.form.validation.required"),
  email: zRequiredEmail("common.form.validation.required", "error.invalidEmail"),
  phone: z.string().optional(),
  address: z.object({
    line1: zRequiredString("common.form.validation.required"),
    line2: z.string().optional(),
    postalCode: zRequiredString("common.form.validation.required"),
    city: zRequiredString("common.form.validation.required"),
    country: zRequiredString("common.form.validation.required"),
  }),

  message: zRequiredString("common.form.validation.required"),
  // acceptTerms: z.boolean().refine((val) => val === true, {
  //   message: "error.terms.required",
  // }),
});

export type RequestAQuoteFormData = z.infer<typeof requestAQuoteSchema>;

export interface RequestAQuoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    data: RequestAQuoteFormData,
    helpers: {
      setError: UseFormSetError<RequestAQuoteFormData>;
      onClose: () => void;
    },
  ) => Promise<void>;
  termsUrl: string;
}

const defaultValues: RequestAQuoteFormData = {
  company: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: {
    line1: "",
    line2: "",
    postalCode: "",
    city: "",
    country: "",
  },
  message: "",
  // acceptTerms: false,
};

export const RequestAQuoteDialog = ({ open, onClose, onSubmit, termsUrl }: RequestAQuoteDialogProps) => {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    setError,
    formState: { isSubmitting, errors },
    reset,
  } = useForm<RequestAQuoteFormData>({
    defaultValues,
    resolver: zodResolver(requestAQuoteSchema),
  });

  const internalSubmit = async (data: RequestAQuoteFormData) => {
    await onSubmit(data, {
      setError,
      onClose: () => {
        reset();
        onClose();
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="desktop-s"
      fullWidth
      slotProps={{
        paper: {
          component: "form",
          onSubmit: handleSubmit(internalSubmit),
          // @ts-expect-error - this property is valid here
          noValidate: true,
        },
      }}
    >
      <DialogHeader onClose={onClose}>
        <DialogTitle sx={{ padding: 0 }}>{t("feature.configurator.requestQuote.title")}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <RequestAQuoteForm control={control} errors={errors} termsUrl={termsUrl} />
      </DialogContent>
      <DialogActions sx={{ mt: 3, px: 3, pb: 3 }}>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          {t("feature.configurator.requestQuote.btn.cancel")}
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          // disabled={isSubmitting}
        >
          {t("feature.configurator.requestQuote.btn.send")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
